import { Fragment, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@/components/Icon';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { searchApi } from '@/lib/apiClient';
import type { Alert, RunSummary } from '@/types/sturm';

export interface Crumb {
  label: string;
  to?: string;
}

export function Topbar({ crumbs, alerts }: { crumbs: Crumb[]; alerts: Alert[] }) {
  const navigate = useNavigate();
  const { data: user } = useCurrentUser();
  const firing = alerts.filter((a) => a.state === 'firing').length;

  return (
    <div className="topbar">
      <div className="breadcrumbs">
        {crumbs.map((crumb, index) => (
          <Fragment key={`${crumb.label}-${index}`}>
            {index > 0 ? (
              <span className="crumb-sep">
                <Icon name="chevron" size={12} />
              </span>
            ) : null}
            <span
              className={`crumb ${index === crumbs.length - 1 ? 'current' : ''}`}
              onClick={crumb.to ? () => navigate(crumb.to!) : undefined}
              style={{ cursor: crumb.to ? 'pointer' : 'default' }}
            >
              {crumb.label}
            </span>
          </Fragment>
        ))}
      </div>

      <GlobalSearch />

      <button
        className="btn btn-icon btn-secondary"
        title="Notifications"
        onClick={() => navigate('/notifications')}
        style={{ position: 'relative' }}
      >
        <Icon name="bell" size={15} />
        {firing > 0 ? (
          <span
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: 'var(--error)',
              boxShadow: '0 0 0 2px var(--white)',
            }}
          />
        ) : null}
      </button>

      <div
        className="tile"
        style={{ width: 30, height: 30, background: 'var(--ink-900)', color: 'var(--white)', fontSize: 12 }}
        title={user?.displayName ?? user?.userName ?? undefined}
      >
        {user?.initials ?? '··'}
      </div>
    </div>
  );
}

function GlobalSearch() {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<RunSummary[]>([]);
  const [open, setOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        inputRef.current?.focus();
      }
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (term.trim().length < 2) {
      setHits([]);
      return;
    }

    const handle = window.setTimeout(() => {
      searchApi
        .query(term.trim())
        .then((results) => {
          setHits(results);
          setOpen(true);
        })
        .catch(() => setHits([]));
    }, 250);

    return () => window.clearTimeout(handle);
  }, [term]);

  return (
    <div className="search" style={{ position: 'relative' }}>
      <Icon name="search" size={14} />
      <input
        ref={inputRef}
        placeholder="Search runs by id, key, or workflow…"
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onFocus={() => hits.length > 0 && setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
      />
      <span className="kbd">⌘ K</span>

      {open && hits.length > 0 ? (
        <div
          className="card"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            left: 0,
            right: 0,
            zIndex: 40,
            maxHeight: 320,
            overflowY: 'auto',
            boxShadow: 'var(--shadow-lg)',
          }}
        >
          {hits.map((hit) => (
            <div
              key={hit.id}
              className="linkrow"
              onMouseDown={() => {
                setOpen(false);
                setTerm('');
                navigate(`/workflows/${hit.workflowSlug}/runs/${hit.id}`);
              }}
            >
              <Icon name="history" size={15} />
              <div className="flex1">
                <div className="label mono">{hit.runKey}</div>
                <div className="sub">
                  {hit.workflowName}
                  {hit.businessKey ? ` · ${hit.businessKey}` : ''}
                </div>
              </div>
              <Icon name="chevron" size={13} />
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}
